import {
    AppUser,
    IActivityCategory,
    IComponent,
    IEventrActivity,
    IOptionsWithTypes,
    spError,
    V2,
} from '@eventr/common'
import { ColoursObj, TEventrIcons } from '@eventr/common-storybook'
import { isArray } from 'underscore'
import { sectionTranslation } from '../i18n/translateHelpers'
import { getPubItineraryName } from '../state/itineraryState/itineraryMap.slice'
import { ComponentFinder } from './componentFinder'
export const publicColour = ColoursObj.tags?.find(
    (c) => c.name === 'Indigo'
)?.hex
export const getDataArray = (data: V2): any[] => {
    return data.data[Object.keys(data.data)[0]] || []
}

export const parseValueToString = (val: any) => {
    switch (typeof val) {
        case 'number':
            return val.toString()
        case 'object':
            return JSON.stringify(val)
        default:
            return val
    }
}

// add one day if format like 2023-03-15
export const addOneDayIfDateType = (date: string) => {
    if (date.length === 10) {
        const dateParts = date.split('-')
        const year = parseInt(dateParts[0])
        const month = parseInt(dateParts[1]) - 1
        const day = parseInt(dateParts[2])
        const newDate = new Date()
        newDate.setUTCFullYear(year)
        newDate.setUTCMonth(month)
        newDate.setUTCDate(day)
        newDate.setUTCHours(23)
        newDate.setUTCMinutes(59)
        newDate.setUTCSeconds(59)

        return newDate.toISOString()
    }
    return date
}

export const getCaptializedName = (userSelected: any) => {
    return (
        userSelected?.firstName.charAt(0).toUpperCase() +
        userSelected?.firstName.slice(1) +
        ' ' +
        (userSelected?.lastName.charAt(0).toUpperCase() +
            userSelected?.lastName.slice(1))
    )
}

export const renderComponents = (
    d: any,
    components: IComponent[],
    past?: boolean
) => {
    return (
        <>
            {components.map((c, i) => {
                return (
                    <ComponentFinder
                        key={'finder_' + c.componentKey + i}
                        data={d}
                        renderOptions={c}
                        componentKey={c.componentKey}
                        isInPast={past}
                    />
                )
            })}
        </>
    )
}

export function getIconName(sectionId: number): TEventrIcons {
    switch (sectionId) {
        case 1: {
            return 'Info'
        }
        case 2: {
            return 'Flight'
        }
        case 3: {
            return 'Stays'
        }
        case 4: {
            return 'Hire_Car'
        }
        case 5: {
            return 'Bus'
        }
        case 8: {
            return 'Activity'
        }
        case 9: {
            return 'Parking'
        }
        default:
            return 'Activity'
    }
}

export const getAllUsers = (path: string[], d: any): any[] => {
    if (path.length === 1) return d[path[0]]
    if (Array.isArray(d[path[0]])) {
        const allUsers = []
        for (let i = 0; i < d[path[0]].length; i++) {
            const el = d[path[0]][i]

            const users = getAllUsers(path.slice(1), el) ?? []
            if (users.length) {
                allUsers.push(...users)
            } else {
                allUsers.push(users)
            }
        }
        return allUsers
    } else if (typeof d[path[0]] === 'object') {
        return getAllUsers(path.slice(1), d[path[0]]) ?? []
    }

    return []
}

export const isUserPartOfUsersWithTags = (
    wpUser: AppUser,
    userWithTags: IOptionsWithTypes[]
) => {
    try {
        if (userWithTags) {
            return userWithTags.some((u: IOptionsWithTypes) => {
                if (u.type === 'User') {
                    return u.option === wpUser.id
                }
                if (u.type === 'Department') {
                    return wpUser.departmentTags?.some((t) => {
                        return t === u.option
                    })
                }
                if (u.type === 'Group') {
                    return wpUser.groupTags?.some((t) => {
                        return t === u.option
                    })
                }
                return false
            })
        }
        return false
    } catch (error) {
        spError(error)
        return false
    }
}

interface IImportedItinerarySection {
    name: string
    itinCode: string
    data: V2[]
}
export const splitImportedData = (
    v2Data?: V2[],
    categories?: IActivityCategory[]
) => {
    const imported: V2[] = []
    const notImported =
        v2Data?.map((d) => {
            if (d?.sectionID === 8) {
                if (!(d.data?.activities ?? []).length) {
                    return d
                }

                if (isArray(d.data.activities)) {
                    const importedActivities: IEventrActivity[] = []
                    const notImported = {
                        ...d,
                        data: {
                            ...d.data,
                            activities: d.data.activities.filter(
                                (a: IEventrActivity) => {
                                    if (a?._og_code === undefined) {
                                        return true
                                    }
                                    importedActivities.push(a)
                                    return false
                                }
                            ),
                            usePage: true,
                        },
                    }
                    if (importedActivities.length) {
                        imported.push({
                            ...d,
                            data: { ...d.data, activities: importedActivities },
                        })
                    }
                    return notImported
                }
            }
            return d
        }) ?? []

    const split: { [key: string]: IEventrActivity[] } = {}
    if (!imported.length) return { split, notImported }
    ;(imported[0].data?.activities ?? []).forEach(
        (activity: IEventrActivity) => {
            const source: string = activity?._og_code || ''
            if (!source || !split[source]) split[source] = []
            split[source].push(activity)
        }
    )
    const splitSections: IImportedItinerarySection[] = []
    for (const key in split) {
        const data = imported[0]
        const activityV2 = {
            ...data,
            sectionNameOverwrite: sectionTranslation('Activities', {
                count: 2,
            }),
            data: { ...data.data, activities: split[key] },
            usePage: true,
            ogCode: key,
        } as V2
        const categorizedActivities = categoriesActivities(
            [activityV2],
            categories
        )

        splitSections.push({
            name: getPubItineraryName(key) ?? 'Public Itinerary',
            itinCode: key,
            data: categorizedActivities,
        })
    }

    return { splitSections, notImported }
}

export const splitTodosData = (v2Data?: V2[]) => {
    const todos: any[] = []
    const allOtherData =
        v2Data?.map((d) => {
            if (d?.sectionID === 8) {
                if (!(d.data?.activities ?? []).length) {
                    return d
                }

                if (isArray(d.data.activities)) {
                    d.data.activities = d.data.activities.filter((a: any) => {
                        if (a?.type === 'todo') {
                            todos.push(a)
                            return false
                        }
                        return true
                    })

                    return d
                }
            }
            return d
        }) ?? []
    return { todos, allOtherData }
}

export const categoriesActivities = (
    v2Data?: V2[],
    categories?: IActivityCategory[]
) => {
    if (!v2Data) return []
    const activitiesSectionIndex = v2Data.findIndex((d) => d.sectionID === 8)
    if (activitiesSectionIndex !== -1)
        v2Data[activitiesSectionIndex].usePage = true

    if (!categories || categories.length === 0) return v2Data
    const categoryMap = categories.reduce((acc, cat) => {
        acc[cat.pub_id] = cat
        return acc
    }, {} as { [key: string]: IActivityCategory })

    if (activitiesSectionIndex === -1) {
        return v2Data // No activities section found
    }

    const originalActivitiesSection = v2Data[activitiesSectionIndex]
    const originalActivities =
        (originalActivitiesSection?.data?.activities as IEventrActivity[]) ?? []

    if (!originalActivities.length) {
        return v2Data // No activities to categorize
    }

    const activitiesByCategoryId: { [key: string]: IEventrActivity[] } = {}
    const uncategorizedActivities: IEventrActivity[] = []

    originalActivities.forEach((activity) => {
        const activityCategories = activity.categories
        let categorized = false // Flag to track if activity was added to any category

        if (isArray(activityCategories) && activityCategories.length > 0) {
            activityCategories.forEach((categoryId) => {
                if (categoryMap[categoryId]) {
                    // Check if the category exists in our map
                    if (!activitiesByCategoryId[categoryId]) {
                        activitiesByCategoryId[categoryId] = []
                    }
                    // Avoid adding the same activity multiple times to the same category list if it somehow has duplicate category IDs
                    if (
                        !activitiesByCategoryId[categoryId].includes(activity)
                    ) {
                        activitiesByCategoryId[categoryId].push(activity)
                    }
                    categorized = true // Mark as categorized
                }
            })
        }

        // If the activity was not added to any category, add it to uncategorized
        if (!categorized) {
            uncategorizedActivities.push(activity)
        }
    })

    const newSections: V2[] = []

    // Create a section for uncategorized activities if any exist
    if (uncategorizedActivities.length > 0) {
        const uncategorizedSection: V2 = JSON.parse(
            JSON.stringify(originalActivitiesSection)
        )
        uncategorizedSection.data = {
            ...uncategorizedSection.data,
            activities: uncategorizedActivities,
        }
        uncategorizedSection.usePage = true
        newSections.push(uncategorizedSection)
    }
    // Create sections for categorized activities
    for (const categoryIdStr in activitiesByCategoryId) {
        const categoryName = categoryMap[categoryIdStr].name
        const categoryIcon = categoryMap[categoryIdStr].icon
        const activities = activitiesByCategoryId[categoryIdStr]

        if (activities.length > 0) {
            // Deep copy the original section structure
            const newSection: V2 = JSON.parse(
                JSON.stringify(originalActivitiesSection)
            )
            newSection.sectionNameOverwrite = categoryName
            newSection.category = categoryIdStr
            newSection.iconOverwrite = categoryIcon
            newSection.usePage = true
            newSection.colour = categoryMap[categoryIdStr].colour
            newSection.data = {
                ...newSection.data,
                activities: activities,
            }
            newSections.push(newSection)
        }
    }

    // Combine original data (excluding original activities section) with new sections
    const result = [
        ...v2Data.slice(0, activitiesSectionIndex),
        ...newSections,
        ...v2Data.slice(activitiesSectionIndex + 1),
    ]
    return result
}
